/**
 * trusted 端末が見る「承認待ち新デバイス」一覧 hook (#819 B-main Phase 2b).
 *
 * Phase 2a (#826) の `/devices/pending`(**trusted device からのみ**・
 * 未信頼 caller は 403)を呼び、登録済 trusted 端末側で
 * `DeviceApprovalPromptModal` を出すための一覧を提供する。
 *
 * - `enabled`(= flag deviceApprovalEnabled かつ自端末 trusted)が true の
 *   ときだけ fetch。trusted でない端末は backend が 403 を返すので呼ばない。
 * - **poll**(#837 demo 検証で判明): trusted 端末が「後から現れた pending
 *   端末」をリロード無しで気付けるよう `POLL_MS` 間隔で再取得する(待機側
 *   `useDeviceApprovalGate` と対称。これが無いと trusted 端末は再 mount
 *   するまで承認 prompt が出ず、承認フローが実質機能しない)。
 *   `enabled=false`(flag OFF/未 trusted)は fetch も poll もしない =
 *   byte-for-byte 不変。
 * - **fail-open**: 取得失敗は「pending 無し」に倒す(trusted 端末を
 *   network 一時障害で block しない。useDeviceConfirmGate と同方針)。
 * - approve/deny 後は `refresh()` で即時再取得。空になれば gate 解除。
 * - #832 churn 回避と同方針: pending 集合が不変なら setState せず
 *   (同一参照を返し)余計な re-render を起こさない。
 *
 * setState は **async コールバック内のみ**(effect 内同期 setState は
 * cascading-render を招くため禁止 = eslint set-state-in-effect)。
 * 無効/device-id 不可時は state を触らず**派生値で `[]` を返す**。
 * loading は consumer (RequireAuth) 不使用のため保持しない。
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../services/api'
import { getDeviceId } from '../lib/deviceId'

export interface PendingDevice {
  deviceId: string
  deviceLabel?: string | null
  firstSeen?: unknown
  lastIpAddress?: string
  lastUserAgent?: string
  deniedAt?: unknown
}

// trusted 端末が新規 pending を検知するまでの最大遅延。待機側
// useDeviceApprovalGate と同じ 4s に揃える(対称・予測可能)。
const POLL_MS = 4000

/** pending 集合が実質同じか(順序非依存 deviceId + deniedAt 有無で比較)。 */
function samePending(a: PendingDevice[], b: PendingDevice[]): boolean {
  if (a.length !== b.length) return false
  const key = (p: PendingDevice) => `${p.deviceId}:${p.deniedAt ? 1 : 0}`
  const sa = a.map(key).sort()
  const sb = b.map(key).sort()
  return sa.every((v, i) => v === sb[i])
}

export function useDevicePendingApprovals(enabled: boolean): {
  pending: PendingDevice[]
  refresh: () => void
} {
  const [fetched, setFetched] = useState<PendingDevice[]>([])
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    let did = ''
    try {
      did = getDeviceId()
    } catch {
      did = ''
    }
    if (!did) return

    const fetchPending = () => {
      api
        .listPendingDevices(did)
        .then((r) => {
          if (cancelled) return
          // 否認済(deniedAt 付)は triage 不要なので除外。未信頼の真の
          // 承認待ちのみ出す。値不変なら同一参照を返し re-render を抑制。
          const next = (r?.pending || []).filter((p) => !p.deniedAt)
          setFetched((prev) => (samePending(prev, next) ? prev : next))
        })
        .catch(() => {
          // fail-open: trusted 端末を詰まらせない
          if (!cancelled) setFetched((prev) => (prev.length === 0 ? prev : []))
        })
    }

    fetchPending()
    // trusted 端末が「後から現れた pending 端末」をリロード無しで気付く
    // ための poll(これが無いと承認フローが実質機能しない。#837 demo 検証)。
    const timer = setInterval(fetchPending, POLL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [enabled, tick])

  // 無効時は派生で空(state を effect 内同期更新しないため)。
  return { pending: enabled ? fetched : [], refresh }
}
